// @flow

import _ from 'lodash';
import I18n from '../../i18n/I18n';

import ApiObject from '../ApiObject';
import ArrayUtils from '../../utils/ArrayUtils';
import CategoryItem from './CategoryItem';
import ProductPrices from './ProductPrices';
import MenuPart from './MenuPart';
import MenuPartProduct from './MenuPartProduct';
import AvailabilityDates from './AvailabilityDates';
import AvailabilityHours from './AvailabilityHours';
import CartModifier from './CartModifier';

import { getLocalizedData } from '../../utils/CatalogUtils';

export default class Menu extends ApiObject {
  type = CategoryItem.Type.MENU;

  id: string = '';

  name: string = '';

  localizedNames: Array<> = [];

  description: string = '';

  localizedDescriptions: Array<> = [];

  image: string = '';

  parts: Array<MenuPart> = [];

  prices: ProductPrices = new ProductPrices();

  availabilityDates: ?AvailabilityDates = null;

  availabilityHours: ?AvailabilityHours = null;

  cartModifiers: Array = [];

  // a front only var set by RestaurantMenu when filtered to indicate that the menu should be
  // display as unavailable
  unavailable: boolean = false;

  allergen = [];

  set name(name) {
    this._name = name;
  }

  get name() {
    return getLocalizedData(this.localizedNames) || this._name || '';
  }

  set description(description) {
    this._description = description;
  }

  get description() {
    return (
      getLocalizedData(this.localizedDescriptions) || this._description || ''
    );
  }

  postJsonAssignation(data: Object) {
    this.prices = new ProductPrices();
    this.prices.fromJson(data.prices);

    if (!_.isNil(data.availabilityDates)) {
      this.availabilityDates = new AvailabilityDates().fromJson(
        data.availabilityDates
      );
    }

    if (!_.isNil(data.availabilityHours)) {
      this.availabilityHours = new AvailabilityHours().fromJson(
        data.availabilityHours
      );
    }

    this.parts = [];
    _.each(data.parts, (partData: Object) => {
      const part: MenuPart = new MenuPart();
      part.fromJson(partData);
      this.parts.push(part);
    });
  }

  // ---- utils
  isActivatedForMode(mode: OrderMode) {
    return this.prices.getMode(mode).available;
  }

  isAvailableForMode(mode: OrderMode, products: Array<Product>): boolean {
    // TODO: verify that at least one product by part is `isAvailableForMode`.
    return this.isActivatedForMode(mode);
  }

  toggleActivationForMode(mode: OrderMode) {
    this.prices.getMode(mode).available = !this.prices.getMode(mode).available;
  }

  getPriceForMode(mode: OrderMode): number {
    return this.prices.getMode(mode).priceIncludingTax;
  }

  getCartModifiersPrice(mode: OrderMode): number {
    let price = this.getPriceForMode(mode);

    if (this.cartModifiers.length > 0) {
      let priceTemp = 0,
        firstCartModifier = true;

      this.cartModifiers.map(cartModifier => {
        if (priceTemp === 0) {
          priceTemp = price;
        }

        if (cartModifier.isHandled && cartModifier.isAvailable) {
          if (
            cartModifier.type === CartModifier.ActionType.DISCOUNT_PERCENTAGE
          ) {
            priceTemp = parseFloat(
              ((priceTemp * (100 - cartModifier.value)) / 100).toFixed(2)
            );
          } else {
            priceTemp = parseFloat((priceTemp - cartModifier.value).toFixed(2));
          }
        }

        if (!cartModifier.combinable && firstCartModifier) {
          firstCartModifier = false;
          return false;
        }
      });

      price = priceTemp;
    }

    return Math.max(0, price);
  }

  haveProduct(productId: string): boolean {
    let res = false;
    _.forEach(this.parts, (part: MenuPart) => {
      const found = _.find(part.products, (partProduct: MenuPartProduct) => {
        return productId === partProduct.id;
      });

      if (!_.isUndefined(found)) {
        res = true;
        return; // quit the loop
      }
    });

    return res;
  }

  getPartWithId(partId: string) {
    return ArrayUtils.find(this.parts, { id: partId });
  }

  /**
   *
   */
  haveStock(): boolean {
    return this.stock !== 0;
  }

  haveStockAndIsActivated(): boolean {
    return this.haveStock() && this.isActivatedForOneMode();
  }

  isActivatedForDate(date: Moment): boolean {
    return (
      _.isNil(this.availabilityDates) ||
      this.availabilityDates.isAvailableForDate(date)
    );
  }

  isAvailableForDate(date: Moment): boolean {
    const available =
      _.isNil(this.availabilityHours) ||
      this.availabilityHours.isAvailableForDate(date);

    if (!available) {
      console.log('menu unavailable', this);
    }

    return available;
  }

  //
  // Validation
  //

  /**
   * Verify that the object is correct
   */
  isValid(): boolean {
    return (
      this.validateName() &&
      this.validateDescription() &&
      this.validatePrices() &&
      this.validateParts()
    );
  }

  isValidManagedByPos(): boolean {
    return this.validateName() && this.validateDescription();
  }

  validateName(): boolean {
    if (_.isEmpty(this.localizedNames)) {
      return false;
    }

    let isValid = false;

    _.forEach(this.localizedNames, localizedName => {
      if (!_.isEmpty(localizedName.text)) {
        isValid = true;
      }
    });

    return isValid;
  }

  validateDescription(): boolean {
    let isValid = true;

    _.forEach(this.localizedDescriptions, localizedDescription => {
      if (
        !_.isEmpty(localizedDescription.text) &&
        localizedDescription.text.length < 5
      ) {
        isValid = false;
      }
    });

    return isValid;
  }

  validatePrices(): boolean {
    return this.prices.isValid();
  }

  validateParts(): boolean {
    return !_.isEmpty(this.parts) && this.parts[0].isValid();
  }

  static validationErrors = {
    name: 'Le nom ne peut être vide',
    description:
      'La description doit être vide ou contenir plus de 5 caractères',
    price: 'Un prix et/ou une taxe sont valides',
    parts: 'Il faut au moins une partie valide',
  };

  getValidationErrors(): Object {
    const validationErrors = Menu.validationErrors;
    const errors = {};

    if (!this.validateName()) {
      errors.name = validationErrors.name;
    }

    if (!this.validateDescription()) {
      errors.description = validationErrors.description;
    }

    if (!this.validatePrices()) {
      errors.price = validationErrors.price;
    }

    if (!this.validateParts()) {
      errors.parts = validationErrors.parts;
    }

    return errors;
  }

  //
  //
  //

  updatePart(partToUpdate: MenuPart) {
    this.parts = ArrayUtils.updateObject(this.parts, partToUpdate, {
      id: partToUpdate.id,
    });
  }

  addPart(partToAdd: MenuPart) {
    this.parts.push(partToAdd);
  }

  deletePart(partToDelete: MenuPart) {
    this.parts = ArrayUtils.deleteObject(this.parts, { id: partToDelete.id });
  }

  movePartUp(menuPartToUp: MenuPart) {
    this.parts = ArrayUtils.moveUpObjectOnArray(this.parts, menuPartToUp, {
      id: menuPartToUp.id,
    });
  }

  movePartDown(menuPartToDown: MenuPart) {
    this.parts = ArrayUtils.moveDownObjectOnArray(this.parts, menuPartToDown, {
      id: menuPartToDown.id,
    });
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/menu/Menu.js